import { useMemo, useState } from 'react';
import { Landmark, Pencil } from 'lucide-react';

import type { AtualizarFonteTribunalInput, FonteTribunal } from '@/api/fontesTribunais';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@/components/ui/PageHeader';
import { SearchInput } from '@/components/ui/search-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useNotification } from '@/context/NotificationProvider';
import { useAtualizarFonteTribunal, useFontesTribunais } from '@/hooks/useFontesTribunais';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { EditarUrlFonteDialog } from './EditarUrlFonteDialog';
import {
  bloqueioConferencia,
  bloqueioPeticionamento,
  filtrarFontes,
  rotuloGrau,
  type FiltroFontes,
} from './fontesTribunais';

interface InterruptorProps {
  label: string;
  checked: boolean;
  bloqueio: string | null;
  pendente: boolean;
  onChange: (valor: boolean) => void;
}

function Interruptor({ label, checked, bloqueio, pendente, onChange }: InterruptorProps) {
  return (
    <div className="flex min-h-11 items-center gap-2">
      <Switch
        checked={checked}
        disabled={bloqueio !== null || pendente}
        onCheckedChange={onChange}
        aria-label={label}
      />
      {bloqueio && <span className="text-xs text-muted-foreground">{bloqueio}</span>}
    </div>
  );
}

export default function FontesTribunaisPage() {
  const { showSuccess, showError } = useNotification();
  const { data, isLoading, error } = useFontesTribunais();
  const atualizar = useAtualizarFonteTribunal();

  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState<FiltroFontes>('com_processos');
  const [editando, setEditando] = useState<FonteTribunal | null>(null);
  const [ligarPeticionamento, setLigarPeticionamento] = useState<FonteTribunal | null>(null);

  const fontes = useMemo(() => filtrarFontes(data ?? [], busca, filtro), [data, busca, filtro]);
  // Uma alteração por vez: com uma mutation só, um segundo mutate() descartaria os callbacks
  // (toast, erro) do primeiro e destravaria a linha dele com o PATCH ainda em curso.
  const ocupada = atualizar.isPending;

  const alterar = (fonte: FonteTribunal, input: AtualizarFonteTribunalInput, mensagem: string) => {
    atualizar.mutate(
      { idFonte: fonte.id_fonte, input },
      {
        onSuccess: () => {
          showSuccess(mensagem);
          setLigarPeticionamento(null);
        },
        onError: (err) => {
          setLigarPeticionamento(null);
          notifyMutationError(err, showError);
        },
      },
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fontes dos tribunais"
        subtitle="URL do sistema processual de cada tribunal e grau, a conferência dos autos com o tribunal e o peticionamento direto no sistema quando a PDPJ falha."
        icon={<Landmark />}
      />

      <Alert>
        <AlertDescription>
          Com a conferência ligada, ao abrir os autos o JurisApp entra no sistema do tribunal com o certificado do
          advogado e traz o que o Data Lake do CNJ ainda não tem. Ligue só depois de validar a fonte.
        </AlertDescription>
      </Alert>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchInput
          value={busca}
          onChange={setBusca}
          placeholder="Buscar tribunal, sistema ou URL"
          className="sm:max-w-sm"
        />
        <Select value={filtro} onValueChange={(v) => setFiltro(v as FiltroFontes)}>
          <SelectTrigger className="sm:w-56" aria-label="Filtrar fontes">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="com_processos">Com processos</SelectItem>
            <SelectItem value="habilitadas">Ligadas</SelectItem>
            <SelectItem value="todas">Todas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading && <Spinner size={32} label="Carregando fontes" />}

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{classifyError(error).message}</AlertDescription>
        </Alert>
      )}

      {!isLoading && !error && fontes.length === 0 && (
        <EmptyState
          icon={<Landmark />}
          title="Nenhuma fonte"
          description={busca || filtro !== 'todas' ? 'Nenhuma fonte com esse filtro.' : 'O catálogo de fontes está vazio.'}
        />
      )}

      {fontes.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tribunal</TableHead>
                  <TableHead>Sistema</TableHead>
                  <TableHead className="text-right">Processos</TableHead>
                  <TableHead>URL do sistema</TableHead>
                  <TableHead>Conferência</TableHead>
                  <TableHead>Peticionamento</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fontes.map((fonte) => {
                  return (
                    <TableRow key={fonte.id_fonte}>
                      <TableCell>
                        <div className="font-medium">{fonte.tribunal_sigla ?? '—'}</div>
                        <div className="text-xs text-muted-foreground">{rotuloGrau(fonte.instancia)}</div>
                      </TableCell>
                      <TableCell>{fonte.sistema_processual ?? '—'}</TableCell>
                      <TableCell className="text-right tabular-nums">{fonte.tramitacoes}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <span
                            className={`max-w-[16rem] truncate ${fonte.url ? '' : 'text-muted-foreground'}`}
                            title={fonte.url ?? undefined}
                          >
                            {fonte.url ?? 'Sem URL'}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-11 w-11 shrink-0"
                            onClick={() => setEditando(fonte)}
                            aria-label={`Editar a URL do sistema de ${fonte.tribunal_sigla ?? ''} ${rotuloGrau(fonte.instancia)}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Interruptor
                          label="Conferência com o tribunal"
                          checked={fonte.consulta_nativa_habilitada}
                          bloqueio={bloqueioConferencia(fonte)}
                          pendente={ocupada}
                          onChange={(valor) =>
                            alterar(
                              fonte,
                              { consulta_nativa_habilitada: valor },
                              valor ? 'Conferência ligada.' : 'Conferência desligada.',
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Interruptor
                          label="Peticionamento no sistema"
                          checked={fonte.peticionamento_nativo_habilitado}
                          bloqueio={bloqueioPeticionamento(fonte)}
                          pendente={ocupada}
                          onChange={(valor) =>
                            valor
                              ? setLigarPeticionamento(fonte)
                              : alterar(fonte, { peticionamento_nativo_habilitado: false }, 'Peticionamento desligado.')
                          }
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {editando && (
        <EditarUrlFonteDialog key={editando.id_fonte} fonte={editando} onClose={() => setEditando(null)} />
      )}

      <ConfirmDialog
        open={ligarPeticionamento !== null}
        title="Ligar o peticionamento no sistema do tribunal?"
        message={
          ligarPeticionamento
            ? `Se o protocolo pela PDPJ falhar sem ter chegado ao tribunal, a petição em ${ligarPeticionamento.tribunal_sigla ?? ''} ${rotuloGrau(ligarPeticionamento.instancia)} é enviada direto pelo sistema deste endereço.`
            : ''
        }
        confirmLabel="Ligar"
        loading={atualizar.isPending}
        onConfirm={() =>
          ligarPeticionamento &&
          alterar(ligarPeticionamento, { peticionamento_nativo_habilitado: true }, 'Peticionamento ligado.')
        }
        onCancel={() => setLigarPeticionamento(null)}
      />
    </div>
  );
}
